import ko from "knockout";
import _ from "lodash";
import navHelper from "services/navHelper";
import ViewModel from "viewModels/ViewModel";
import { getHashRoute } from "util/uriUtil";
import featureToggleService, {
    FeatureToggle,
} from "services/featureToggleService";
import loggingService from "services/loggingService";

const listMenuItemsByHashLength = function (navItems, sortedAccumulator) {
    const sortedItems = sortedAccumulator || [];
    _.forEach(navItems, function (navItem) {
        if (navItem.sectionTitle) {
            listMenuItemsByHashLength(navItem.menuItems, sortedItems);
        } else {
            const insertIndex = _.sortedIndexBy(
                sortedItems,
                navItem,
                function (item) {
                    const hashValue = ko.unwrap(item.hash);
                    return hashValue.length;
                }
            );
            sortedItems.splice(insertIndex, 0, navItem);
        }
    });
    return sortedItems;
};

/**
 * A ViewModel to help with the management of displaying a navigation menu.
 *
 * @param {array} navItems - an array of objects that must adhere to following
 * structure:
 *
 *     {
 *       hash: "#/route"     // A string representing the route that the browser
 *                           // should be set to when this item is selected
 *
 *       label: "messageKey" // A message key whose value is the text that will
 *                           // display for the nav button
 *
 *       cssClass: "string"    // (optional) A string with a space-separated list of css class names to apply.
 *
 *       tabPlacement: "number" // (optional) Desired tab order, from lowest (first) to highest (last)
 *                              // If no value, or -1 is given then the tab will be added to the end of the list.
 *     }
 */
const NavMenu = ViewModel.extend(function (navItems) {
    const self = this;
    self.tabList = null;
    self.navItems = ko
        .observableArray()
        .extend({ rateLimit: 100, method: "notifyWhenChangesStop" });

    /**
     * Add a gux-tab element to the tablist element.
     * @param {Object} navItem - The navItem to add.
     * @param {Element} insertBeforeElement - The element to insert the new tab before.
     */
    const addGuxTabElement = function (navItem, insertBeforeElement) {
        if (typeof navItem?.tabId !== "string" || navItem.tabId.trim() === "") {
            loggingService.warn(`Invalid tabId provided: ${navItem?.tabId}`);
            return;
        }

        if (self.tabList.querySelector(`gux-tab[tab-id="${navItem.tabId}"]`)) {
            // Avoid adding a duplicate tab.
            loggingService.warn(
                `Tab with tabId ${navItem.tabId} already exists`
            );
            return;
        }

        try {
            const newTabElement = document.createElement("gux-tab");
            newTabElement.setAttribute("tab-id", navItem.tabId);
            newTabElement.appendChild(document.createElement("span"));
            newTabElement.textContent = ko.i18n.renderString(navItem.label);

            navItem.clickEventHandler = (event) => {
                event.preventDefault();
                event.stopImmediatePropagation();
                navHelper.navigateTo(navItem.href);
            };
            newTabElement.addEventListener("click", navItem.clickEventHandler);

            if (insertBeforeElement instanceof Element) {
                insertBeforeElement.before(newTabElement);
            } else {
                self.tabList.appendChild(newTabElement);
            }
        } catch (error) {
            loggingService.error(
                `Error adding tab with tabId ${navItem.tabId}: ${error}`
            );
        }
    };

    self.initGuxTabList = function (element) {
        self.tabList = element;

        /*
         * Before the tablist is added in the DOM, there could be some navItems already added.
         * So we have to add them into the DOM.
         */
        self.navItems().forEach(addGuxTabElement);
    };

    /**
     * Handles changes to the navItems observable array and synchronizes the gux-tab DOM elements.
     * This callback is invoked by Knockout's arrayChange subscription whenever items are
     * added, removed, or reordered in the navItems array.
     *
     * @param {Array<Object>} changes - Array of change objects from Knockout's arrayChange subscription.
     * @param {('added'|'deleted'|'retained')} changes[].status - Type of change: 'added' for new items,
     *        'deleted' for removed items, 'retained' for items that remain but may have moved position.
     * @param {Object} changes[].value - The navItem object containing tabId, label, and href properties.
     * @param {number} changes[].index - Zero-based index position of the item in the array after the change.
     * @returns {void}
     */
    self.subscribe(
        self.navItems,
        (changes) => {
            if (!self.tabList) {
                // Tablist is not initialized yet, so we will not be able to add or remove tabs.
                return;
            }

            changes.forEach(function (change) {
                const navItem = change.value;
                switch (change.status) {
                    case "added": {
                        const existingTab = self.tabList.querySelector(
                            `gux-tab[tab-id="${navItem?.tabId}"]`
                        );
                        if (!existingTab) {
                            const childTabs =
                                self.tabList.querySelectorAll("gux-tab");
                            addGuxTabElement(navItem, childTabs[change.index]);
                        }
                        break;
                    }
                    case "deleted": {
                        const tabToRemove = self.tabList.querySelector(
                            `gux-tab[tab-id="${navItem?.tabId}"]`
                        );
                        if (tabToRemove && navItem?.clickEventHandler) {
                            tabToRemove.removeEventListener(
                                "click",
                                navItem.clickEventHandler
                            );
                            navItem.clickEventHandler = null;
                        }
                        tabToRemove?.remove();
                        break;
                    }
                    default:
                        break;
                }
            });
        },
        null,
        "arrayChange"
    );

    // Sets the active item to the longest one matching the hash tag.
    // NOTE: A regular expression can be used.
    self.setActive = function (hash) {
        if (hash && hash.length > 0) {
            // Given items of '#test123' and "#test123/456", if the route's hash is
            // '#test123/456', they will both match, but the longer one should be activated.
            const sortedItems = listMenuItemsByHashLength(self.navItems());
            let foundActive = false;
            // Find the active item, starting with the longest hash. Set all
            // others to inactive.
            _.forEachRight(sortedItems, function (item) {
                const pattern = new RegExp("^" + item.localHash);
                if (!foundActive && pattern.test(hash)) {
                    item.active(true);
                    foundActive = true;
                } else {
                    item.active(false);
                }
            });
        }
    };

    self.addNavItem = function (navItem) {
        if (navItem.sectionTitle) {
            _.forEach(navItem.menuItems, function (menuItem) {
                menuItem.active = ko.observable(false);
                menuItem.computedCssClass = self.computed(function () {
                    let cssClass = ko.unwrap(menuItem.cssClass) || "";
                    if (menuItem.active()) {
                        cssClass = cssClass
                            ? cssClass + " " + "active"
                            : "active";
                    }
                    return cssClass;
                });
                //if an href option was provided separate from hash, use it, otherwise use the hash option
                menuItem.href =
                    menuItem.href || navHelper.asHostUrl(menuItem.hash);
            });
        } else {
            navItem.active = ko.observable(false);
            navItem.computedCssClass = self.computed(function () {
                let cssClass = ko.unwrap(navItem.cssClass) || "";
                if (navItem.active()) {
                    cssClass = cssClass ? cssClass + " " + "active" : "active";
                }
                return cssClass;
            });
            //if an href option was provided separate from hash, use it, otherwise use the hash option
            navItem.href = navItem.href || navHelper.asHostUrl(navItem.hash);
        }

        self.navItems.push(navItem);

        //Sort the tab order by tabPlacement value. If given -1 then sort to the end of the array.
        self.navItems.sort(function (left, right) {
            return left.tabPlacement === right.tabPlacement
                ? 0
                : left.tabPlacement < 0
                ? 1
                : right.tabPlacement < 0
                ? -1
                : left.tabPlacement < right.tabPlacement
                ? -1
                : 1;
        });
    };

    self.containsNavItem = function (navItem) {
        return !!_.find(self.navItems(), { hash: navItem.hash });
    };

    self.removeNavItem = function (navItem) {
        const matchedNavItem = _.find(self.navItems(), { hash: navItem.hash });
        if (matchedNavItem) {
            self.navItems.remove(matchedNavItem);
        }
    };

    // Initialize from constructor parameter
    _.forEach(navItems, function (navItem) {
        self.addNavItem(navItem);
    });

    // Set inital state.
    self.setActive(getHashRoute());
});

export default NavMenu;
